// Phase A.3/A.4/8A.3 — Single chat message bubble with inline card support.
//
// Supports: assistant (SIKA) bubbles, user bubbles, proactive bubbles,
// and inline tool-use cards. Proactive messages get a gold left border
// and a "Proactive insight" label per Design Doc §4.5.

import Markdown from "react-markdown";

import type { ChatMessage } from "../../sika/useSikaChat.ts";
import { SikaCardRenderer } from "./cards/SikaCardRenderer.tsx";
import { SikaAgentDeploy, parseAgentSteps } from "./SikaAgentDeploy.tsx";

interface Props {
  msg: ChatMessage;
  userInitial: string;
  /** Chat send function for actionable cards (ACTIVATE, ADJUST, SAVE). */
  onAction?: (text: string) => void;
  /** True while SIKA is still responding — hides card action buttons. */
  sending?: boolean;
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function getCardData(msg: ChatMessage): unknown | null {
  if (!msg.card_type) return null;
  const tu = msg.tool_use as { result?: unknown } | null;
  return tu?.result ?? null;
}

/** Detect proactive messages — sent by the trigger system, not in response
 *  to user input. Currently heuristic: content starts with "📊" or "🔔" or
 *  "⚡" or metadata.is_proactive flag when available. */
function isProactive(msg: ChatMessage): boolean {
  if (msg.role !== "assistant") return false;
  const tu = msg.tool_use as { is_proactive?: boolean } | null;
  if (tu?.is_proactive) return true;
  // Heuristic until backend adds the flag
  const c = msg.content;
  return c.startsWith("📊") || c.startsWith("🔔") || c.startsWith("⚡");
}

export function SikaChatMessage({ msg, userInitial, onAction, sending }: Props): JSX.Element {
  const isSika = msg.role === "assistant";
  const isCard = msg.role === "tool" && msg.card_type != null;
  const cardData = getCardData(msg);
  const proactive = isProactive(msg);
  const agentSteps = isSika ? parseAgentSteps(msg.tool_use) : null;

  // Tool messages without renderable card data → hide entirely
  if (msg.role === "tool") {
    if (!cardData) return <></>;
  }

  if (isCard && cardData != null) {
    return (
      <div className="msg sika">
        <div className="av">S</div>
        <div style={{ maxWidth: 560 }}>
          <SikaCardRenderer cardType={msg.card_type!} data={cardData} onAction={onAction} sending={sending} />
          <div className="ts">{formatTime(msg.created_at)}</div>
        </div>
      </div>
    );
  }

  // Hide assistant messages with no text content (tool_use-only messages)
  if (isSika && !msg.content?.trim() && (!agentSteps || agentSteps.length === 0)) {
    return <></>;
  }

  const msgClass = isSika
    ? `msg sika${proactive ? " proactive" : ""}`
    : "msg user";

  return (
    <div className={msgClass}>
      <div className="av">{isSika ? "S" : userInitial}</div>
      <div>
        {proactive && <div className="proactive-label">Proactive insight</div>}
        <div className="bubble">
          {isSika ? <Markdown>{msg.content}</Markdown> : msg.content}
          {agentSteps && agentSteps.length > 0 && (
            <SikaAgentDeploy steps={agentSteps} variant="inline" />
          )}
        </div>
        <div className="ts">{formatTime(msg.created_at)}</div>
      </div>
    </div>
  );
}
